import { useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import Breadcrumb from '@/components/layout/Breadcrumb'
import Card from '@/components/ui/Card'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { Book, BookOpen, Headphones, List } from 'lucide-react'
import { cn } from '@/utils/cn'
import NavigationPanel from '@/components/quran/NavigationPanel'
import MushafSpread from '@/components/quran/MushafSpread'
import SurahFilters, { type FilterType } from '@/components/quran/SurahFilters'
import SurahCard from '@/components/quran/SurahCard'
import ContinueReadingSection from '@/components/quran/ContinueReadingSection'
import ReadingView from '@/components/quran/ReadingView'
import TranslationView from '@/components/quran/TranslationView'
import AudioPlayer from '@/components/quran/AudioPlayer'
import { quranApi, type Surah, type SurahDetail } from '@/services/quranApiService'
import { mockSurahs } from '@/services/mockDataService'

type ViewMode = 'mushaf' | 'ayah'
type ContentTab = 'index' | 'reading' | 'translation' | 'audio'

export default function QuranPage() {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [viewMode, setViewMode] = useState<ViewMode>(
    (searchParams.get('mode') as ViewMode) || 'ayah'
  )
  const [activeTab, setActiveTab] = useState<ContentTab>(
    (searchParams.get('tab') as ContentTab) || 'index'
  )
  const [currentPage, setCurrentPage] = useState<number>(
    parseInt(searchParams.get('page') || '1', 10)
  )
  const [currentSurah, setCurrentSurah] = useState<number>(
    parseInt(searchParams.get('surah') || '1', 10)
  )
  const [currentJuz, setCurrentJuz] = useState<number>(
    parseInt(searchParams.get('juz') || '1', 10)
  )

  // For Index tab
  const [surahs, setSurahs] = useState<Surah[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [isLoadingSurahs, setIsLoadingSurahs] = useState(false)

  // For Reading/Translation/Audio tabs
  const [surahDetail, setSurahDetail] = useState<SurahDetail | null>(null)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Word highlighting for audio
  const [highlightedAyah, setHighlightedAyah] = useState<number | undefined>()
  const [highlightedWord, setHighlightedWord] = useState<number | undefined>()

  // Fetch surahs list for index
  useEffect(() => {
    const fetchSurahs = async () => {
      try {
        setIsLoadingSurahs(true)
        setError(null)
        const data = await quranApi.getSurahs(searchQuery || undefined)
        setSurahs(data)
      } catch (err) {
        console.error('Failed to fetch surahs:', err)
        setError(t('quran.error.loadSurahsFailed', 'فشل تحميل قائمة السور'))
        setSurahs(mockSurahs.map((s) => ({
          id: s.number,
          number: s.number,
          name: s.name,
          nameEn: s.nameEn,
          ayahs: s.ayahs,
          revelationPlace: s.revelationPlace,
          revelationOrder: s.revelationOrder,
          juz: s.juz,
        })))
      } finally {
        setIsLoadingSurahs(false)
      }
    }
    if (activeTab === 'index') {
      fetchSurahs()
    }
  }, [activeTab, searchQuery, t])

  // Fetch surah detail for reading/translation/audio tabs
  useEffect(() => {
    const fetchSurahDetail = async () => {
      if (activeTab === 'index') return

      try {
        setIsLoadingDetail(true)
        setError(null)
        const lang = i18n.language.split('-')[0] || 'en'
        const data = await quranApi.getSurah(currentSurah, lang)
        setSurahDetail(data)
        localStorage.setItem('lastReadSurahNumber', currentSurah.toString())
      } catch (err) {
        console.error('Failed to fetch surah:', err)
        setError(t('quran.error.loadSurahFailed', 'فشل تحميل بيانات السورة'))
      } finally {
        setIsLoadingDetail(false)
      }
    }
    if (viewMode === 'ayah') {
      fetchSurahDetail()
    }
  }, [activeTab, currentSurah, viewMode, i18n.language, t])

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams()
    params.set('mode', viewMode)
    params.set('tab', activeTab)
    if (viewMode === 'mushaf') {
      params.set('page', currentPage.toString())
      params.set('juz', currentJuz.toString())
    } else {
      params.set('surah', currentSurah.toString())
    }
    setSearchParams(params, { replace: true })
  }, [viewMode, activeTab, currentPage, currentSurah, currentJuz, setSearchParams])

  // Filter surahs
  const filteredSurahs = useMemo(() => {
    let result = [...surahs]
    switch (filterType) {
      case 'revelation':
        result.sort((a, b) => a.revelationOrder - b.revelationOrder)
        break
      case 'meccan':
        result = result.filter((s) => s.revelationPlace === 'Mecca')
        break
      case 'medinan':
        result = result.filter((s) => s.revelationPlace === 'Medina')
        break
    }
    return result
  }, [surahs, filterType])

  // Get last read surah info
  const lastReadSurahNumber = parseInt(localStorage.getItem('lastReadSurahNumber') || '1', 10)
  const lastReadSurah = surahs.find((s) => s.number === lastReadSurahNumber)
  const lastReadIndex = lastReadSurah ? surahs.findIndex((s) => s.number === lastReadSurah.number) : -1
  const previousSurah = lastReadIndex > 0 ? surahs[lastReadIndex - 1] : null
  const nextSurah = lastReadIndex < surahs.length - 1 ? surahs[lastReadIndex + 1] : null

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.quran', 'القرآن الكريم') },
  ]

  const tabs = [
    { id: 'index' as ContentTab, label: t('quran.index', 'الفهرس'), icon: List },
    { id: 'reading' as ContentTab, label: t('quran.reading', 'القراءة'), icon: Book },
    { id: 'translation' as ContentTab, label: t('quran.translation', 'الترجمة'), icon: BookOpen },
    { id: 'audio' as ContentTab, label: t('quran.audio', 'الاستماع'), icon: Headphones },
  ]

  return (
    <>
      <Helmet>
        <title>{t('common.quran', 'القرآن الكريم')} - سكينة</title>
        <meta
          name="description"
          content={t('quran.description', 'اقرأ القرآن الكريم واستمع إليه بتلاوات متنوعة مع الترجمة والتفسير')}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-4 sm:mb-6" />

        {/* Header with Title and View Mode Toggle */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
              {t('common.quran', 'القرآن الكريم')}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              {t('quran.subtitle', 'اقرأ واستمع وتدبر كلام الله')}
            </p>
          </div>

          {/* View Mode Toggle */}
          <Card className="p-1 flex gap-1 w-full sm:w-auto">
            <button
              onClick={() => {
                setViewMode('ayah')
                // Switch to reading tab when clicking عرض الآيات
                if (activeTab === 'index') {
                  setActiveTab('reading')
                }
              }}
              className={cn(
                'flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors',
                viewMode === 'ayah'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              {t('quran.ayahMode', 'عرض الآيات')}
            </button>
            {/* <button
              onClick={() => setViewMode('mushaf')}
              className={cn(
                'flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors',
                viewMode === 'mushaf'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              {t('quran.mushafMode', 'عرض المصحف')}
            </button> */}
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-4 sm:mb-6 overflow-x-auto">
          <nav className="flex gap-4 sm:gap-6 md:gap-8 min-w-max sm:min-w-0" aria-label="Quran Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-1 sm:gap-2 pb-3 sm:pb-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap',
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden xs:inline">{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="min-h-[600px]">
          {/* Index Tab - Surah List */}
          {activeTab === 'index' && (
            <div className="space-y-6">
              {/* Continue Reading Section */}
              {lastReadSurah && (
                <ContinueReadingSection
                  currentSurah={lastReadSurah}
                  previousSurah={previousSurah}
                  nextSurah={nextSurah}
                />
              )}

              {/* Search and Filters */}
              <SurahFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filterType={filterType}
                onFilterChange={setFilterType}
              />

              {/* Surah Grid */}
              {isLoadingSurahs ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : error && !surahs.length ? (
                <Card className="p-6">
                  <p className="text-center text-red-600">{error}</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredSurahs.map((surah) => (
                    <div
                      key={surah.number}
                      onClick={() => {
                        setCurrentSurah(surah.number)
                        setActiveTab('reading')
                      }}
                      className="cursor-pointer"
                    >
                      <SurahCard surah={surah} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Reading Tab */}
          {activeTab === 'reading' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Navigation Sidebar */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <NavigationPanel
                  currentPage={currentPage}
                  currentSurah={currentSurah}
                  currentJuz={currentJuz}
                  viewMode={viewMode}
                  onPageChange={setCurrentPage}
                  onSurahChange={setCurrentSurah}
                  onJuzChange={setCurrentJuz}
                />
              </div>

              {/* Content Area */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                {viewMode === 'mushaf' ? (
                  <MushafSpread
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                ) : isLoadingDetail ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : error || !surahDetail ? (
                  <Card className="p-6">
                    <p className="text-center text-red-600">{error || t('quran.error.surahNotFound', 'لم يتم العثور على السورة')}</p>
                  </Card>
                ) : (
                  <ReadingView
                    ayahs={surahDetail.ayahsList.map((a) => ({
                      number: a.number,
                      text: a.arabicText,
                    }))}
                    highlightedAyah={highlightedAyah}
                    highlightedWord={highlightedWord}
                  />
                )}
              </div>
            </div>
          )}

          {/* Translation Tab */}
          {activeTab === 'translation' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Navigation Sidebar */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <NavigationPanel
                  currentPage={currentPage}
                  currentSurah={currentSurah}
                  currentJuz={currentJuz}
                  viewMode={viewMode}
                  onPageChange={setCurrentPage}
                  onSurahChange={setCurrentSurah}
                  onJuzChange={setCurrentJuz}
                />
              </div>

              {/* Content Area */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : error || !surahDetail ? (
                  <Card className="p-6">
                    <p className="text-center text-red-600">{error || 'لم يتم العثور على السورة'}</p>
                  </Card>
                ) : (
                  <TranslationView
                    ayahs={surahDetail.ayahsList.map((a) => ({
                      number: a.number,
                      arabicText: a.arabicText,
                      translations: {
                        [i18n.language.split('-')[0] || 'en']: a.translation || '',
                      },
                    }))}
                    availableLanguages={[i18n.language.split('-')[0] || 'en']}
                    defaultLanguage={i18n.language.split('-')[0] || 'en'}
                  />
                )}
              </div>
            </div>
          )}

          {/* Audio Tab */}
          {activeTab === 'audio' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Navigation Sidebar */}
              <div className="lg:col-span-1 space-y-3 sm:space-y-4 order-2 lg:order-1">
                <NavigationPanel
                  currentPage={currentPage}
                  currentSurah={currentSurah}
                  currentJuz={currentJuz}
                  viewMode={viewMode}
                  onPageChange={setCurrentPage}
                  onSurahChange={setCurrentSurah}
                  onJuzChange={setCurrentJuz}
                />
                {surahDetail && (
                  <AudioPlayer
                    surahNumber={currentSurah}
                    ayahs={surahDetail.ayahsList.map((a) => ({
                      number: a.number,
                      text: a.arabicText,
                    }))}
                    onWordHighlight={(ayahNumber, wordIndex) => {
                      setHighlightedAyah(ayahNumber)
                      setHighlightedWord(wordIndex)
                    }}
                    onAyahChange={(ayahNumber) => {
                      setHighlightedAyah(ayahNumber)
                      setHighlightedWord(undefined)
                    }}
                  />
                )}
              </div>

              {/* Content Area - Reading view with highlighting */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : error || !surahDetail ? (
                  <Card className="p-6">
                    <p className="text-center text-red-600">{error || 'لم يتم العثور على السورة'}</p>
                  </Card>
                ) : (
                  <ReadingView
                    ayahs={surahDetail.ayahsList.map((a) => ({
                      number: a.number,
                      text: a.arabicText,
                    }))}
                    highlightedAyah={highlightedAyah}
                    highlightedWord={highlightedWord}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

